
import React from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, FileCheck } from 'lucide-react';
import { useAdminVerifications } from '@/hooks/useAdminVerifications';
import { useAdminFilter } from '@/contexts/AdminFilterContext';
import DocumentVerificationFilters from './DocumentVerificationFilters';
import ProfileVerificationCard from './ProfileVerificationCard';
import { toast } from 'sonner';

const AdminDocumentManagement = () => {
  const { userTypeFilter } = useAdminFilter();
  const {
    verifications,
    isLoading,
    error,
    refetch,
    statusFilter,
    setStatusFilter,
    searchTerm,
    setSearchTerm,
    statusCounts,
    handleVerificationAction
  } = useAdminVerifications(userTypeFilter);

  const handleRefresh = async () => {
    try {
      await refetch();
      toast.success('Dados atualizados com sucesso');
    } catch (error) {
      toast.error('Erro ao atualizar dados');
    }
  };

  if (error) {
    return (
      <div className="px-4 py-6">
        <div className="text-center text-red-400">
          <p className="mb-4">Erro ao carregar verificações</p>
          <Button onClick={handleRefresh} variant="outline" className="border-white/20 text-white">
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            Verificação de Documentos
          </h2>
          <p className="text-white/60 text-sm">
            {verifications.length} verificação{verifications.length !== 1 ? 'ões' : ''} 
            {statusFilter !== 'all' && ` • Filtro: ${statusFilter}`}
          </p>
        </div>
        <Button 
          onClick={handleRefresh}
          variant="ghost" 
          size="sm"
          className="text-white hover:bg-white/10"
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Filters */}
      <DocumentVerificationFilters
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusCounts={statusCounts}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-40 bg-white/10 rounded-lg animate-pulse"></div>
          ))}
        </div>
      )}

      {/* Verification Cards */}
      {!isLoading && (
        <div className="space-y-4 pb-24 md:pb-6">
          {verifications.map((verification) => (
            <ProfileVerificationCard
              key={verification.id}
              verification={verification}
              onAction={handleVerificationAction}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && verifications.length === 0 && (
        <div className="text-center py-12">
          <FileCheck className="h-12 w-12 text-white/40 mx-auto mb-4" />
          <p className="text-white/60 text-lg mb-2">
            {searchTerm 
              ? `Nenhuma verificação encontrada para "${searchTerm}"`
              : statusFilter === 'all'
                ? 'Nenhuma verificação encontrada'
                : `Nenhuma verificação com status "${statusFilter}"`
            }
          </p>
          <p className="text-white/40 text-sm">
            {searchTerm || statusFilter !== 'all' 
              ? 'Tente ajustar os filtros de busca'
              : 'As verificações aparecerão aqui quando os usuários enviarem documentos'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminDocumentManagement;
